import {
    OrderItem,
    Solution,
    SolutionPerItemState,
    SolutionState,
    useSupportCaseStore,
} from '../../../store/supportCaseStore';
import {useState} from 'react';
import {
    Button,
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    Icons,
    Typography,
} from '@toppy-webshop/toppy-design-system';
import styles from './rootQuestion.module.css';
import Choice from './Choice';

interface QuestionProps {
    solutions: Array<Solution> | Record<string, Solution[]>;
    state: SolutionState | SolutionPerItemState;
    questionOrderNumber: number;
    cardsLayout?: boolean;
}

const Solutions = ({
    solutions,
    state,
    questionOrderNumber,
    cardsLayout,
}: QuestionProps) => {
    const submitSolution = useSupportCaseStore(state => state.submitSolution);
    const submitSolutionPerItem = useSupportCaseStore(
        state => state.submitSolutionPerItem
    );
    const order = useSupportCaseStore(state => state.supportCase!.order);
    const requiredItems = !Array.isArray(solutions)
        ? Object.keys(solutions)
        : [];
    const [selectedSolutions, setSolutions] = useState<
        Record<string, Solution>
    >({});
    const [isLoading, setIsLoading] = useState(false);
    const isAnswered =
        ('answers' in state && state.answers !== null) ||
        ('answer' in state && state.answer !== null);
    const perItem = !Array.isArray(solutions);

    return (
        <Card shadow={cardsLayout ? undefined : 'none'} noMargin={!cardsLayout}>
            <CardHeader
                noBorder={isAnswered}
                noHorizontalPadding={!cardsLayout}
                noMargin={isAnswered}
            >
                <div className={styles.headerWrapper}>
                    <span className={styles.number}>{questionOrderNumber}</span>{' '}
                    Zo kunnen we je helpen
                </div>
            </CardHeader>
            {!isAnswered && (
                <CardContent>
                    {perItem && requiredItems.length > 1 && (
                        <div style={{marginBottom: '.5rem'}}>
                            <Typography>
                                Selecteer per product wat van toepassing is
                            </Typography>
                        </div>
                    )}
                    {perItem &&
                        requiredItems.map((item, index) => {
                            const orderItem = order.orderItems.find(
                                orderItem => orderItem.identifier.value === item
                            ) as OrderItem;
                            return (
                                <div
                                    style={
                                        index === requiredItems.length - 1
                                            ? {paddingBottom: '0.5rem'}
                                            : {
                                                  borderBottom:
                                                      '1px solid #ccc',
                                                  paddingBottom: '0.5rem',
                                                  marginBottom: '1rem',
                                              }
                                    }
                                >
                                    {requiredItems.length > 1 && (
                                        <div className={styles.itemName}>
                                            <Typography
                                                weight={'bold'}
                                                gutterBottom={true}
                                            >
                                                {orderItem.description}
                                            </Typography>
                                        </div>
                                    )}
                                    {(
                                        state.solutions as Record<
                                            string,
                                            Solution[]
                                        >
                                    )[item].map(solution => {
                                        return (
                                            <Choice
                                                key={solution.id}
                                                choice={solution}
                                                label={solution.label}
                                                description={
                                                    solution.description
                                                }
                                                disabled={isLoading}
                                                checked={
                                                    selectedSolutions?.[item]
                                                        ?.id === solution.id
                                                }
                                                onClick={solution =>
                                                    setSolutions({
                                                        ...selectedSolutions,
                                                        [item]: solution as Solution,
                                                    })
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}
                    {Array.isArray(solutions) &&
                        solutions.map(solution => {
                            return (
                                <Choice
                                    key={solution.id}
                                    choice={solution}
                                    label={solution.label}
                                    description={solution.description}
                                    disabled={isLoading}
                                    checked={
                                        selectedSolutions?.root?.id ===
                                        solution.id
                                    }
                                    onClick={solution =>
                                        setSolutions({
                                            root: solution as Solution,
                                        })
                                    }
                                />
                            );
                        })}
                </CardContent>
            )}
            {!isAnswered && (
                <CardFooter noHorizontalPadding={!cardsLayout}>
                    <Button
                        label={
                            selectedSolutions ? 'Volgende' : 'Maak een keuze'
                        }
                        rightIcon={isLoading ? Icons.LOADING : Icons.CH_RIGHT}
                        type={
                            selectedSolutions && !isLoading
                                ? 'primary'
                                : 'disabled'
                        }
                        onClick={() => {
                            setIsLoading(true);
                            if (!perItem)
                                submitSolution(selectedSolutions.root).finally(
                                    () => setIsLoading(false)
                                );
                            if (perItem)
                                submitSolutionPerItem(
                                    selectedSolutions
                                ).finally(() => setIsLoading(false));
                        }}
                    />
                </CardFooter>
            )}
        </Card>
    );
};

export default Solutions;
